import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { useI18n } from '../../../i18n/I18nContext';

const Icon = ({ name, className }) => {
    const map = {
        'tag': 'bi bi-tag',
        'cpu': 'bi bi-cpu',
        'calculator': 'bi bi-calculator',
        'truck': 'bi bi-truck',
        'geo': 'bi bi-geo-alt',
        'weight': 'bi bi-box-seam',
        'spinner': 'bi bi-arrow-repeat spinning',
        'magic': 'bi bi-magic',
        'check': 'bi bi-check-circle-fill',
        'close': 'bi bi-x',
        'search': 'bi bi-search',
        'info': 'bi bi-info-circle-fill',
        'alert': 'bi bi-exclamation-triangle-fill',
        'wallet': 'bi bi-wallet2'
    };
    return <i className={`${map[name] || `bi bi-${name}`} ${className || ''}`} />;
};

const AiPricingTab = () => {
    const { t } = useI18n();
    const [missions, setMissions] = useState([]);
    
    const [selectedMissionId, setSelectedMissionId] = useState('');
    const [targetMargin, setTargetMargin] = useState(30);
    const [otherCharges, setOtherCharges] = useState(0);
    
    const [loadingMissions, setLoadingMissions] = useState(false);
    const [estimating, setEstimating] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [rawResponse, setRawResponse] = useState(null);

    const getMissionLabel = (m) => {
        if (!m) return '';
        const departureStr = m.expediteur_nom || m.expediteurNom || m.departure_location || m.departureLocation;
        const arrivalStr = m.destinataire_nom || m.destinataireNom || m.arrival_location || m.arrivalLocation;
        let label = `${departureStr || '?'} → ${arrivalStr || '?'}`;
        if (m.expediteur_nom || m.expediteurNom || m.destinataire_nom || m.destinataireNom) {
            label += ` (${m.departure_location || m.departureLocation || '?'} → ${m.arrival_location || m.arrivalLocation || '?'})`;
        }
        return label;
    };

    // Fetch missions immediately on mount
    useEffect(() => {
        loadMissions();
    }, []);

    const loadMissions = async () => {
        setLoadingMissions(true);
        setSelectedMissionId('');
        setError(null);
        try {
            const res = await api.get(`/agent/planning/fraud/pricing/missions?mode=pricing`);
            let missionsList = [];
            if (Array.isArray(res.data)) {
                missionsList = res.data;
            } else if (res.data?.data?.missions) {
                missionsList = res.data.data.missions;
            } else if (res.data?.missions) {
                missionsList = res.data.missions;
            }
            setMissions(missionsList);
        } catch (e) {
            console.error("Erreur lors du chargement des missions:", e);
            setError("Impossible de charger les missions sans prix.");
        } finally {
            setLoadingMissions(false);
        }
    };

    const handleEstimate = async () => {
        if (!selectedMissionId) return;
        setEstimating(true);
        setResult(null);
        setRawResponse(null);
        setError(null);
        try {
            const payload = {
                mission_id: selectedMissionId,
                use_ai: true,
                target_margin_pct: Number(targetMargin),
                manual_other_charges: Number(otherCharges)
            };
            const res = await api.post('/agent/planning/fraud/pricing/estimate', payload);
            setRawResponse(res.data);
            
            if (res.data?.ok === false) {
                setError(res.data?.error || "Erreur lors de la génération de l'estimation.");
            } else {
                setResult(res.data?.data || res.data);
            }
        } catch (e) {
            console.error("Erreur lors de l'estimation:", e);
            setError("Erreur technique lors de la communication avec le serveur d'estimation.");
        } finally {
            setEstimating(false);
        }
    };

    const renderResult = () => {
        if (!result) return null;

        const costs = result.costs || {};
        const dist = result.distance || {};
        const conso = result.consumption || {};
        const ai = result.price_proposal_ai || result.price_proposal_formula || {};
        const currency = ai.devise || result.currency || 'MAD';
        const warnings = Array.isArray(result.warnings) ? result.warnings : [];

        return (
            <div className="fade-in">
                {/* 4 Cards Grid */}
                <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                        <div className="p-3 bg-danger-soft border border-danger-subtle rounded-4 h-100 shadow-sm">
                            <div className="text-muted small fw-semibold text-uppercase mb-1">Prix minimum</div>
                            <div className="h4 fw-bold text-danger mb-0">
                                {ai.prix_minimum?.toLocaleString() || '0'} {currency}
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="p-3 bg-primary-soft border border-primary-subtle rounded-4 h-100 shadow-sm">
                            <div className="text-primary small fw-semibold text-uppercase mb-1">Prix conseillé</div>
                            <div className="h4 fw-bold text-primary mb-0">
                                {ai.prix_recommande?.toLocaleString() || '0'} {currency}
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="p-3 bg-success-soft border border-success-subtle rounded-4 h-100 shadow-sm">
                            <div className="text-muted small fw-semibold text-uppercase mb-1">Prix haut</div>
                            <div className="h4 fw-bold text-success mb-0">
                                {ai.prix_haut?.toLocaleString() || '0'} {currency}
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="p-3 bg-warning-soft border border-warning-subtle rounded-4 h-100 shadow-sm">
                            <div className="text-muted small fw-semibold text-uppercase mb-1">Confiance</div>
                            <div className="h4 fw-bold text-warning mb-0 text-capitalize">
                                {ai.confiance || result.confidence || '—'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 6 Info Boxes Grid */}
                <div className="row g-3 mb-4">
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Mission</div>
                            <div className="fw-semibold text-dark text-truncate">{result.reference_number || result.mission_id || '—'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Trajet</div>
                            <div className="fw-semibold text-dark">{result.departure_location || '?'} → {result.arrival_location || '?'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Coût connu</div>
                            <div className="fw-semibold text-dark">{costs.total_known_cost?.toLocaleString() || '0'} {currency}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Carburant</div>
                            <div className="fw-semibold text-dark">{costs.fuel_cost?.toLocaleString() || '0'} {currency}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Distance</div>
                            <div className="fw-semibold text-dark">{dist.distance_km || '0'} km • {dist.source || '—'}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="bg-light border p-3 rounded-4 shadow-sm">
                            <div className="text-muted small mb-1">Consommation</div>
                            <div className="fw-semibold text-dark">{(conso.consumption_l_100km || 0).toFixed(1)} L/100 • {conso.source || '—'}</div>
                        </div>
                    </div>
                </div>

                {/* Résumé IA Section */}
                <div className="mb-4">
                    <h6 className="fw-bold mb-2 text-dark"><Icon name="cpu" className="text-primary" /> Résumé IA</h6>
                    <div className="p-3 bg-light border rounded-4 text-dark small" style={{ lineHeight: '1.6' }}>
                        {ai.resume || 'Prix proposé avec les données disponibles.'}
                    </div>
                </div>

                {/* Points à vérifier Section */}
                <div className="mb-4">
                    <h6 className="fw-bold mb-2 text-dark"><Icon name="alert" className="text-warning" /> Points à vérifier</h6>
                    {warnings.length ? (
                        <div className="d-flex flex-column gap-2">
                            {warnings.slice(0, 6).map((w, idx) => (
                                <div key={idx} className="p-3 bg-danger-soft border border-danger-subtle rounded-4 d-flex align-items-start gap-2 text-danger small">
                                    <Icon name="alert" className="mt-0.5" />
                                    <span>{w}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-3 bg-light border rounded-4 text-muted text-center small">
                            Aucune alerte.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="pricing-tab fade-in">
            {/* Header */}
            <div className="dashboard-header mb-4">
                <h2 className="fw-bold text-dark mb-1">Prix IA poids lourd</h2>
                <p className="text-muted small mb-0">Choisis une mission sans prix. Mistral propose le prix uniquement pour cette mission.</p>
            </div>

            {error && (
                <div className="alert alert-danger border-0 shadow-sm rounded-4 mb-4 d-flex align-items-center gap-2">
                    <Icon name="alert" className="fs-5" /> 
                    <span>{error}</span>
                </div>
            )}

            <div className="row g-4">
                {/* Left Column: 1. Sélection */}
                <div className="col-lg-4">
                    <div className="acc-card border shadow-sm rounded-4 p-4 h-100">
                        <div className="acc-card-title mb-4">
                            <Icon name="tag" /> 1. Sélection
                        </div>

                        {/* Mission Dropdown */}
                        <div className="acc-field-group mb-3">
                            <label><Icon name="truck" /> Mission sans prix</label>
                            <select 
                                className="form-select py-2 px-3 border rounded-3"
                                value={selectedMissionId}
                                onChange={(e) => setSelectedMissionId(e.target.value)}
                                disabled={loadingMissions}
                            >
                                {loadingMissions ? (
                                    <option value="">Chargement des missions...</option>
                                ) : missions.length === 0 ? (
                                    <option value="">Aucune mission sans prix</option>
                                ) : (
                                    <>
                                        <option value="">-- Choisir une mission --</option>
                                        {missions.map(m => (
                                            <option key={m.mission_id || m.id} value={m.mission_id || m.id}>
                                                {getMissionLabel(m)}
                                            </option>
                                        ))}
                                    </>
                                )}
                            </select>
                        </div>

                        {/* Target Margin */}
                        <div className="acc-field-group mb-3">
                            <label><Icon name="calculator" /> Marge voulue (%)</label>
                            <input 
                                type="number" 
                                className="form-control py-2 px-3 border rounded-3"
                                value={targetMargin}
                                onChange={(e) => setTargetMargin(e.target.value)}
                                min="0" 
                                max="100" 
                                step="0.1"
                            />
                        </div>

                        {/* Other Charges */}
                        <div className="acc-field-group mb-4">
                            <label><Icon name="wallet" /> Autres charges manuelles</label>
                            <input 
                                type="number" 
                                className="form-control py-2 px-3 border rounded-3"
                                value={otherCharges}
                                onChange={(e) => setOtherCharges(e.target.value)}
                                min="0" 
                                step="0.01"
                                placeholder="0 si inconnu"
                            />
                        </div>

                        {/* Action Buttons */}
                        <button 
                            type="button" 
                            className="acc-btn-primary w-100 py-2.5 rounded-3 fw-semibold"
                            onClick={handleEstimate}
                            disabled={estimating || !selectedMissionId}
                        >
                            {estimating ? (
                                <><Icon name="spinner" /> Calcul en cours...</>
                            ) : (
                                <><Icon name="magic" /> Proposer prix</>
                            )}
                        </button>

                        {/* Info Box */}
                        <div className="p-3 bg-primary-soft border border-primary-subtle rounded-4 mt-4 text-dark">
                            <div className="fw-bold small mb-1"><Icon name="info" /> Source données</div>
                            <div className="text-muted extra-small" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                                Distance, consommation et prix carburant sont cherchés automatiquement dans le backend, l’historique véhicule et les règles.
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: 2. Résultat */}
                <div className="col-lg-8">
                    <div className="acc-card border shadow-sm rounded-4 p-4 h-100">
                        <div className="acc-card-title mb-4">
                            <Icon name="magic" /> 2. Résultat
                        </div>

                        {estimating ? (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center text-muted">
                                <Icon name="spinner" className="fs-1 text-primary mb-3" />
                                <div className="fw-bold text-dark mb-1">Mistral prépare le prix pour cette mission uniquement...</div>
                                <div className="small">Calcul en cours, estimation de la marge et vérification de la trajectoire en cours.</div>
                            </div>
                        ) : result ? (
                            <>
                                {renderResult()}
                                
                                {/* Collapsible Technical Details */}
                                <details className="mt-4 pt-3 border-top">
                                    <summary className="text-muted small fw-bold cursor-pointer" style={{ listStyle: 'none', cursor: 'pointer' }}>
                                        ▶ Détail technique
                                    </summary>
                                    <pre className="bg-light p-3 rounded-3 mt-2 text-dark border overflow-auto" style={{ maxHeight: '250px', fontSize: '0.78rem' }}>
                                        {JSON.stringify(rawResponse, null, 2)}
                                    </pre>
                                </details>
                            </>
                        ) : (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center text-muted border border-dashed rounded-4">
                                <Icon name="magic" className="fs-1 mb-3 text-muted text-opacity-50" />
                                <div className="small">Sélectionne une mission sans prix puis clique sur proposer prix.</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                .extra-small { font-size: 0.72rem; }
                .cursor-pointer { cursor: pointer; }
                .bg-light { background-color: #f8f9fa !important; }
            `}} />
        </div>
    );
};

export default AiPricingTab;
